import * as React from 'react';
import { Box } from '@chakra-ui/react';
import { formatDate, isAfter, now } from 'shared-components';
import {
  BankLetterList,
  BankStatementDocList,
  BirthCertificateDocList,
  BirthCertificateDocs,
  DocAccessType,
  FamilySponsorship,
  FamilySponsorshipDoc,
  IDocumentsList,
  IRisk,
  ParentLetterDocList,
} from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getMaturityDate } from '../../../../utilities/validation';

import { formatCurrency } from '../../../../utilities/formatting';
import { UserRole } from '../../../../store/auth/types';

import BankStatement from '../../../sections/financeDocs/bankStatement';
import BirthCertificate from '../../../sections/financeDocs/birthCertificate';
import ParentLetter from '../../../sections/financeDocs/parentLetter';
import BankLetter from '../../../sections/financeDocs/bankLetter';
import { hasFundingRisks, isFundingRisk } from '../../../../utilities/risks';
import {
  bankLetterName,
  bankStatementName,
  bankStatementTransName,
  getFingerStatus,
  getVisible,
  hasDeleted,
} from '../../../../utilities/documents';
import { accountTypeLabel, familySponsorTypeLabelShort } from '../../../organisms/finance/BankSponsor/utils';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { SummaryGroup } from '../../../Summary/SummaryGroup';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  id: string;
  documents: FamilySponsorshipDoc;
  financials: FamilySponsorship;
  risk: IRisk['fundingRisk']['familySponsorship'];
  docRisk: IRisk['financeDocsRisk'];
  birthCertificate?: BirthCertificateDocs;
  isDeleting: boolean;
  userRole?: UserRole;

  canCheckOff?: boolean;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;

  isCheckedOffDocRisks?: (docType: string, docKey: string) => boolean;
  isCheckedOffFinanceRisks?: (docType: string, docKey: string) => boolean;
  onCheckOffDocRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onCheckOffFinanceRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onResume: (url: string) => void;
  onUploadClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onOpenClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onApproveClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onRescindRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onDeleteClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onAcknowledgeClick: (sponsorType: string, docType: string | null, docKey: string) => void;
}

const FamilySection = (props: IProps) => {
  const { showRisks, financials, infoOnly, userAccess, userRole } = props;
  const familySponsor = props.documents;
  const isFather = financials.accountType === 'Father';
  const isMother = financials.accountType === 'Mother';
  const isGuardian = financials.accountType === 'Guardian';
  const isSpouse = financials.accountType === 'Spouse';

  const getMetaData = () => {
    return (
      <SummaryGroup title="Bank sponsor" key={`family_sponsorship_${props.id}`} boxProps={{ as: 'div', pt: 0 }}>
        <SummaryRow
          key={`family_sponsor_name_${props.id}`}
          label={`Bank account owner`}
          fullLabel="Whose bank account?"
          value={accountTypeLabel(props.financials.accountType!)}
          isRisk={showRisks && isFundingRisk(props.risk, 'accountType', props.id)}
        />

        <SummaryRow
          key={`family_sponsor_type_${props.id}`}
          label={`Account type`}
          fullLabel="What is the account type?"
          value={familySponsorTypeLabelShort(props.financials.sponsorType!)}
          isRisk={showRisks && isFundingRisk(props.risk, 'sponsorType', props.id)}
        />

        {props.financials.accountType === 'Other' ? (
          <SummaryRow
            key={`family_sponsor_name_other_${props.id}`}
            label={`Reason for choosing 'other'`}
            fullLabel="Please explain why you've chosen 'other'?"
            value={props.financials.otherReason!}
            isRisk={showRisks && isFundingRisk(props.risk, 'otherReason', props.id)}
          />
        ) : (
          <></>
        )}

        <SummaryRow
          key={`family_country_bank_account_${props.id}`}
          label={`Bank account country`}
          fullLabel="Country bank account is located"
          value={props.financials.country!}
          isRisk={showRisks && isFundingRisk(props.risk, 'country', props.id)}
        />

        {props.financials.bankName ? (
          <>
            <SummaryRow
              key={`family_country_bank_name_${props.id}`}
              label={`Bank Name`}
              fullLabel="Please select the bank"
              value={props.financials.bankName}
              isRisk={showRisks && isFundingRisk(props.risk, 'bankName', props.id)}
            />

            <SummaryRow
              key={`family_bank_name_accepted_${props.id}`}
              label={'Bank risk alert'}
              value={props.financials.isBankAccepted! ? 'None' : 'Caution'}
              isRisk={showRisks && isFundingRisk(props.risk, 'bankName', props.id)}
            />
          </>
        ) : null}

        <SummaryRow
          key={`family_total_in_account_${props.id}`}
          label={`Current bank balance`}
          fullLabel="What is the current balance of the account in pounds?"
          value={`£${formatCurrency(props.financials.totalAmount!)}`}
          isRisk={showRisks && isFundingRisk(props.risk, 'totalAmount', props.id)}
        />

        <SummaryRow
          key={`education_loan_contribution_amount_${props.id}`}
          label={'Contribution amount'}
          value={`£${formatCurrency(props.financials.sponsorshipAmount)}`}
          isRisk={showRisks && false}
        />
      </SummaryGroup>
    );
  };

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];
  const { bankStatement } = familySponsor;
  const bankTranslation = familySponsor.bankStatementTranslation;
  const { bankLetter } = familySponsor;
  const { parentLetter } = familySponsor;
  const { birthCertificate } = props;
  const { verificationBankLetter } = familySponsor;
  const { secondaryBankStatement } = familySponsor;

  let fundsMaturedError = null;
  if (birthCertificate && birthCertificate.certificate) {
    const visibleDocs = getVisible({
      documentsHash: birthCertificate.certificate!,
      userAccess,
      infoOnly,
      userRole,
    }) as BirthCertificateDocList[];
    if (hasDeleted(birthCertificate.certificate)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BirthCertificateDocList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`birth-certificate-${key}`}
              key={`birth-certificate-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`Birth Certificate or Proof of Guardianship ${
                Object.keys(birthCertificate).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''
              }`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('birthCertificate', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('birthCertificate', key, 'Birth Certificate')
              }
              onUploadClick={() =>
                props.onUploadClick(
                  'birthCertificate',
                  'certificate',
                  key,
                  'Birth Certificate or Proof of Guardianship',
                )
              }
              onOpenClick={() => props.onOpenClick('birthCertificate', 'certificate', key)}
              onApproveClick={() => props.onApproveClick('birthCertificate', 'certificate', key)}
              onRejectClick={() => {
                props.onRejectClick('birthCertificate', 'certificate', key, `Guardian's Birth Certificate Scan`);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('birthCertificate', 'certificate', key, `Guardian's Birth Certificate Scan`)
              }
              onDeleteClick={() => {
                props.onDeleteClick('birthCertificate', 'certificate', key, `Guardian's Birth Certificate Scan`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('birthCertificate', 'certificate', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <BirthCertificate
                  id={key}
                  showRisks={showRisks}
                  risk={props.docRisk.birthCertificate}
                  isLegalDocument={item.isLegalDocument!}
                  hasAllNames={item.hasAllNames!}
                  inEnglish={item.isInEnglish!}
                  hasTranslation={item.hasProvidedTranslation!}
                  uploadedBy={item.uploadedBy}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (birthCertificate && birthCertificate.translation) {
    const visibleDocs = getVisible({
      documentsHash: birthCertificate.translation,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(birthCertificate.translation)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          return (
            <FingerWithApprove
              id={`birth-certificate-translation-${key}`}
              key={`birth-certificate-translation-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`Birth Certificate Translation ${
                Object.keys(birthCertificate).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''
              }`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('birthCertificate', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks('birthCertificate', key, 'Birth Certificate Translation')
              }
              onUploadClick={() =>
                props.onUploadClick('birthCertificate', 'translation', key, 'Birth Certificate Translation')
              }
              onOpenClick={() => props.onOpenClick('birthCertificate', 'translation', key)}
              onApproveClick={() => props.onApproveClick('birthCertificate', 'translation', key)}
              onRejectClick={() => {
                props.onRejectClick('birthCertificate', 'translation', key, `Birth Certificate Translation`);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('birthCertificate', 'translation', key, `Birth Certificate Translation`)
              }
              onDeleteClick={() => {
                props.onDeleteClick('birthCertificate', 'translation', key, `Birth Certificate Translation`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('birthCertificate', 'translation', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (parentLetter) {
    const visibleDocs = getVisible({
      documentsHash: parentLetter,
      userAccess,
      infoOnly,
      userRole,
    }) as ParentLetterDocList[];
    if (hasDeleted(parentLetter)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `Letter from your ${isMother ? 'Mother' : isFather ? 'Father' : 'Guardian'}`;
      docNodes.push(
        ...visibleDocs.map((doc: ParentLetterDocList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`letter-from-guardian-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${modalTitle}${Object.keys(parentLetter).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''}`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('familySponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks(
                  'familySponsorship',
                  props.id + key,
                  `Letter from your ${isMother ? 'Mother' : isFather ? 'Father' : 'Guardian'}`,
                )
              }
              onUploadClick={() => props.onUploadClick('familySponsorship', 'parentLetter', key, modalTitle)}
              onOpenClick={() => props.onOpenClick('familySponsorship', 'parentLetter', key)}
              onApproveClick={() => props.onApproveClick('familySponsorship', 'parentLetter', key)}
              onRejectClick={() => {
                props.onRejectClick('familySponsorship', 'parentLetter', key, modalTitle);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('familySponsorship', 'parentLetter', key, modalTitle)
              }
              onDeleteClick={() => {
                props.onDeleteClick('familySponsorship', 'parentLetter', key, modalTitle);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('familySponsorship', 'parentLetter', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <ParentLetter
                  id={props.id + key}
                  showRisks={showRisks}
                  risk={props.docRisk.familySponsorship}
                  selectedTextBodyReq={item.selectedTextBodyReq}
                  isSigned={item.isSigned}
                  hasDate={item.hasDate}
                  dateOnLetter={item.dateOnLetter}
                  inEnglish={item.isInEnglish}
                  hasTranslation={item.hasProvidedTranslation}
                  uploadedBy={item.uploadedBy}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (bankStatement) {
    const visibleDocs = getVisible({
      documentsHash: bankStatement,
      userAccess,
      infoOnly,
      userRole,
    }) as BankStatementDocList[];
    if (hasDeleted(bankStatement)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BankStatementDocList, index: number) => {
          const { key, item } = doc;

          const maturityDate =
            item.belowReqAmount && item.dateBelowReqAmount ? getMaturityDate(item.dateBelowReqAmount) : null;
          fundsMaturedError =
            maturityDate && isAfter(maturityDate, now()) ? formatDate(maturityDate, 'DD/MM/YYYY') : null;

          const title = isMother
            ? `Mother's`
            : isFather
              ? `Father's`
              : isGuardian
                ? `Guardian's`
                : isSpouse
                  ? `Partner's`
                  : `My`;
          const docName = bankStatementName(financials.sponsorType);

          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`bank-statement-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${title} ${docName}${visibleDocs.length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''}`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('familySponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks('familySponsorship', props.id + key, `${title} ${docName}`)
              }
              onUploadClick={() =>
                props.onUploadClick('familySponsorship', 'bankStatement', key, `${title} ${docName}`)
              }
              onOpenClick={() => props.onOpenClick('familySponsorship', 'bankStatement', key)}
              onApproveClick={() => props.onApproveClick('familySponsorship', 'bankStatement', key)}
              onRejectClick={() => props.onRejectClick('familySponsorship', 'bankStatement', key, `${docName}`)}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('familySponsorship', 'bankStatement', key, `${docName}`)
              }
              onDeleteClick={() => props.onDeleteClick('familySponsorship', 'bankStatement', key, `${docName}`)}
              onAcknowledgeClick={() => props.onAcknowledgeClick('familySponsorship', 'bankStatement', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <BankStatement
                  id={props.id + key}
                  sponsorType={financials.sponsorType}
                  showRisks={showRisks}
                  risk={props.docRisk.familySponsorship}
                  isPaperBankStatement={item.isPaperBankStatement}
                  isLogoShown={item.isLogoShown}
                  isDateShown={item.isDateShown}
                  isNameAccShown={item.isNameAccShown}
                  isInEnglish={item.isInEnglish}
                  hasProvidedTranslation={item.hasProvidedTranslation}
                  currency={item.currency}
                  hasAmountInAccount={item.hasAmountInAccount}
                  belowReqAmount={item.belowReqAmount}
                  dateBelowReqAmount={item.dateBelowReqAmount}
                  hasRunningTransactions={item.hasRunningTransactions}
                  hasClosingBalanceDate={item.hasClosingBalanceDate}
                  dateOfClosingBalance={item.dateOfClosingBalance}
                  uploadedBy={item.uploadedBy}
                  sponsorshipAmount={financials.sponsorshipAmount}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (bankLetter) {
    const visibleDocs = getVisible({ documentsHash: bankLetter, userAccess, infoOnly, userRole }) as BankLetterList[];
    if (hasDeleted(bankLetter)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BankLetterList, index: number) => {
          const { key, item } = doc;
          const docName = bankLetterName(financials.sponsorType);

          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`letter-from-bank-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${docName}${Object.keys(bankLetter).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''}`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('familySponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('familySponsorship', props.id + key, `${docName}`)
              }
              onUploadClick={() => props.onUploadClick('familySponsorship', 'bankLetter', key, `${docName}`)}
              onOpenClick={() => props.onOpenClick('familySponsorship', 'bankLetter', key)}
              onApproveClick={() => props.onApproveClick('familySponsorship', 'bankLetter', key)}
              onRejectClick={() => {
                props.onRejectClick('familySponsorship', 'bankLetter', key, `${docName}`);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('familySponsorship', 'bankLetter', key, `${docName}`)
              }
              onDeleteClick={() => {
                props.onDeleteClick('familySponsorship', 'bankLetter', key, `${docName}`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('familySponsorship', 'bankLetter', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <BankLetter
                  id={props.id + key}
                  showRisks={showRisks}
                  risk={props.docRisk.familySponsorship}
                  hasOfficialLetterhead={item.hasOfficialLetterhead!}
                  nameAndLogoVisible={item.nameAndLogoVisible!}
                  officiallySigned={item.officiallySigned!}
                  hasDate={item.hasDate!}
                  dateOnLetter={item.dateOnLetter!}
                  selectedTextBodyReq={item.selectedTextBodyReq!}
                  inEnglish={Boolean(item.isInEnglish!)}
                  hasTranslation={Boolean(item.hasProvidedTranslation!)}
                  uploadedBy={item.uploadedBy}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (bankTranslation) {
    const visibleDocs = getVisible({
      documentsHash: bankTranslation,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(bankTranslation)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          const docName = bankStatementTransName(financials.sponsorType);
          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`bank-translation-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${docName}${Object.keys(bankTranslation).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''}`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('familySponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('familySponsorship', props.id + key, `${docName}`)
              }
              onUploadClick={() =>
                props.onUploadClick('familySponsorship', 'bankStatementTranslation', key, `${docName}`)
              }
              onOpenClick={() => props.onOpenClick('familySponsorship', 'bankStatementTranslation', key)}
              onApproveClick={() => props.onApproveClick('familySponsorship', 'bankStatementTranslation', key)}
              onRejectClick={() => {
                props.onRejectClick('familySponsorship', 'bankStatementTranslation', key, `${docName}`);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('familySponsorship', 'bankStatementTranslation', key, `${docName}`)
              }
              onDeleteClick={() => {
                props.onDeleteClick('familySponsorship', 'bankStatementTranslation', key, `${docName}`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('familySponsorship', 'bankStatementTranslation', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (verificationBankLetter) {
    const visibleDocs = getVisible({
      documentsHash: verificationBankLetter,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(verificationBankLetter)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          const docName = 'Verification Letter from Bank';
          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`verification-bank-letter-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${docName}${
                Object.keys(verificationBankLetter).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''
              }`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('familySponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('familySponsorship', props.id + key, `${docName}`)
              }
              onUploadClick={() => props.onUploadClick('familySponsorship', 'verificationBankLetter', key, docName)}
              onOpenClick={() => props.onOpenClick('familySponsorship', 'verificationBankLetter', key)}
              onApproveClick={() => props.onApproveClick('familySponsorship', 'verificationBankLetter', key)}
              onRejectClick={() => {
                props.onRejectClick('familySponsorship', 'verificationBankLetter', key, docName);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('familySponsorship', 'verificationBankLetter', key, docName)
              }
              onDeleteClick={() => {
                props.onDeleteClick('familySponsorship', 'verificationBankLetter', key, docName);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('familySponsorship', 'verificationBankLetter', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (secondaryBankStatement) {
    const visibleDocs = getVisible({
      documentsHash: secondaryBankStatement,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(secondaryBankStatement)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          const docName = 'Secondary Bank Statement Scan';
          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`secondary-bank-statement-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`${docName}${
                Object.keys(secondaryBankStatement).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''
              }`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('familySponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('familySponsorship', props.id + key, `${docName}`)
              }
              onUploadClick={() => props.onUploadClick('familySponsorship', 'secondaryBankStatement', key, docName)}
              onOpenClick={() => props.onOpenClick('familySponsorship', 'secondaryBankStatement', key)}
              onApproveClick={() => props.onApproveClick('familySponsorship', 'secondaryBankStatement', key)}
              onRejectClick={() => {
                props.onRejectClick('familySponsorship', 'secondaryBankStatement', key, docName);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('familySponsorship', 'secondaryBankStatement', key, docName)
              }
              onDeleteClick={() => {
                props.onDeleteClick('familySponsorship', 'secondaryBankStatement', key, docName);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('familySponsorship', 'secondaryBankStatement', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={`family-sponsorship-${props.id}`}
      label={`Myself or My Family`}
      isRisk={showRisks && hasFundingRisks(props.risk, props.id)}
      isRiskSection={showRisks && hasFundingRisks(props.risk, props.id)}
      canCheckOff={props.canCheckOff}
      isCheckedOff={() =>
        props.isCheckedOffFinanceRisks ? props.isCheckedOffFinanceRisks('familySponsorship', props.id) : false
      }
      onCheckOffRisk={() =>
        props.onCheckOffFinanceRisks &&
        props.onCheckOffFinanceRisks('familySponsorship', props.id, 'Family Sponsorship')
      }
      canEdit={props.canResume}
      edit={() => {
        props.onResume('finances/step-1');
      }}
      metaData={getMetaData()}
      amount={`£${formatCurrency(props.financials.sponsorshipAmount)}`}
    >
      {hasDocs ? (
        <>
          {fundsMaturedError ? <Box color={'error'}>Finances mature after {fundsMaturedError}</Box> : null}

          {docNodes}
        </>
      ) : (
        <NoDocuments />
      )}
    </Section>
  );
};

export default FamilySection;
